/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import { useServiceContext } from '../../../root'
import { useTranslation } from 'react-i18next'
import { useEsignature } from '../../../hooks/useEsignature'
import Modal from '../../../components/Modal'

interface Artifact {
  index: number
  name: string
  selected?: boolean
  disabled?: boolean
  isCopy?: boolean
}

interface EnableDisableArtifactsModalProps {
  file: {
    id: number | string
    name: string
  }
  onClose?: () => void
}

interface ESignature {
  username?: string
  password?: string
  relayState?: string
  [key: string]: unknown
}

const EnableDisableArtifactsModal: React.FC<EnableDisableArtifactsModalProps> = ({ file, onClose }) => {
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { api } = context
  const { t } = useTranslation()
  const { signAction } = useEsignature()
  const [isOpen, setIsOpen] = useState(false)
  const [artifacts, setArtifacts] = useState<Artifact[]>([])
  const [steps, setSteps] = useState(1)
  const [actionType, setActionType] = useState<'enable' | 'disable'>('enable')
  const [sortDesc, setSortDesc] = useState(false)
  const [selectedArtifactsArray, setSelectedArtifactsArray] = useState<Artifact[]>([])
  const [showErrorMessage, setShowErrorMessage] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const loadData = async () => {
    try {
      const data = await api.etmf.getArtifacts(file.id)
      // Handle Restangular .plain() response or direct array
      const artifactsData = Array.isArray(data) ? data : (data as any).plain?.() || []
      // Sort by name
      const sorted = artifactsData.sort((a: Artifact, b: Artifact) => a.name.localeCompare(b.name))
      setArtifacts(sorted)
      setIsOpen(true)
    } catch (error: any) {
      if (error?.message) {
        toast.error(t('FILE_PLAN_ERROR_WITH_MESSAGE', { msg: error?.message }))
      } else {
        toast.error(t('FILE_PLAN_ERROR_GENERAL'))
      }
    }
  }

  const back = () => {
    const currentStep = steps - 1
    setSortDesc(false)
    setShowErrorMessage(false)

    if (currentStep === 1) {
      setSelectedArtifactsArray([])
      if (actionType === 'enable') {
        const updatedArtifact = artifacts.map((artifact) => {
          if (!artifact?.disabled && artifact.selected) {
            return { ...artifact, selected: !artifact.selected }
          } else if (artifact.selected) {
            return { ...artifact, disabled: false }
          }
          return artifact
        })
        setArtifacts(updatedArtifact)
      } else {
        const updatedArtifact = artifacts.map((artifact) => {
          if (!artifact?.disabled) {
            return { ...artifact, selected: true }
          } else if (artifact?.disabled && !artifact.selected) {
            return { ...artifact, disabled: false }
          }
          return artifact
        })
        setArtifacts(updatedArtifact)
      }
    }
    setSteps(currentStep)
  }

  const sortBy = (collectionName: Artifact[], value: keyof Artifact, changeOrder: boolean) => {
    if (changeOrder) {
      setSortDesc((prev) => !prev)
    }
    // Sort by the specified field
    const sorted = [...collectionName].sort((a, b) => {
      const aVal = String(a[value] || '')
      const bVal = String(b[value] || '')
      return aVal.localeCompare(bVal)
    })
    if (!sortDesc) {
      sorted.reverse()
    }
    setArtifacts(sorted)
  }

  const selectAll = (isSelected: boolean) => {
    if (!isSelected) {
      setSelectedArtifactsArray([])
    }
    const allSelected = isAllSelected(artifacts)
    if (!allSelected && selectedArtifactsArray?.length > 0) {
      setSelectedArtifactsArray([])
    }
    const updateArtifacts = artifacts.map((item) => ({
      ...item,
      selected: !item?.disabled ? !allSelected : item.selected
    }))
    updateArtifacts.map((item) => setSelectedArtifacts(item))
    setArtifacts(updateArtifacts)
  }

  const isAllSelected = (collection: Artifact[]) => {
    const notSelectedItems = collection.filter((item) => !item.selected && !item?.disabled)
    return collection?.length > 0 && notSelectedItems?.length === 0
  }

  const switchSelected = (entry: Artifact) => {
    const updateArtifact = { ...entry, selected: !entry.selected }
    if (!entry?.disabled) {
      setArtifacts((item) => item.map((data) => (data.index === updateArtifact.index ? updateArtifact : data)))
    }
    setSelectedArtifactsForSingle(updateArtifact)
  }

  const setSelectedArtifactsForSingle = (artifact: Artifact) => {
    const includedArtifactsArray: Artifact[] = []
    if (!artifact?.disabled && artifact.selected) {
      includedArtifactsArray.push(artifact)
      setSelectedArtifactsArray((prevData) => {
        return [...prevData, ...includedArtifactsArray]
      })
    } else if (!artifact.selected) {
      const modifiedArtifactsArray = selectedArtifactsArray.filter((item) => item.index !== artifact.index)
      setSelectedArtifactsArray(modifiedArtifactsArray)
    }
  }

  const setSelectedArtifacts = (artifact: Artifact) => {
    const includedArtifactsArray: Artifact[] = []
    if (!artifact?.disabled && artifact.selected) {
      includedArtifactsArray.push(artifact)
    }
    setSelectedArtifactsArray((prevData) => {
      return [...prevData, ...includedArtifactsArray]
    })
  }

  const save = async () => {
    let currentStep = steps + 1
    setSortDesc(false)

    if (currentStep === 3 && selectedArtifactsArray?.length === 0) {
      currentStep = currentStep - 1

      setShowErrorMessage(true)
      if (selectedArtifactsArray?.length === 0) {
        return
      }
      let updatedArtifact: Artifact[] = []
      if (actionType === 'disable') {
        updatedArtifact = artifacts.map((item) => {
          if (!item?.disabled) {
            return { ...item, selected: true }
          }
          return item
        })
      }

      setArtifacts(updatedArtifact)
    }

    if (currentStep === 2) {
      let updatedArtifact: Artifact[] = []
      if (actionType === 'enable') {
        updatedArtifact = artifacts.map((item) => {
          if (item.selected) {
            return { ...item, disabled: true }
          }
          return item
        })
      } else if (actionType === 'disable') {
        updatedArtifact = artifacts.map((item) => {
          if (!item.selected) {
            return { ...item, disabled: true }
          }
          return { ...item, selected: false }
        })
      }
      setArtifacts(updatedArtifact)
    }

    if (currentStep === 4) {
      doClose()

      const artifactIndex: number[] = []
      selectedArtifactsArray.forEach(function (artifact) {
        artifactIndex.push(artifact.index)
      })

      const updateArtifacts = async (validation?: ESignature) => {
        try {
          let apiCall
          if (actionType === 'enable') {
            apiCall = api.etmf.enableArtifacts(file.id, artifactIndex, validation)
          } else {
            apiCall = api.etmf.disableArtifacts(file.id, artifactIndex, validation)
          }

          await apiCall
          toast.success(
            t(
              actionType === 'enable'
                ? 'ARTIFACTS_HAVE_BEEN_SUCCESSFULLY_ENABLED_WITH_MISSING_ZONE_SECTION_FOLDER'
                : 'ARTIFACTS_HAVE_BEEN_SUCCESSFULLY_DISABLED'
            )
          )
          window.dispatchEvent(new CustomEvent('bctable:updateTable'))
        } catch (error: any) {
          toast.error(t('ERROR_ERROR', { error: error?.data?.message || error?.message }))
        }
      }

      if (auth.requireESignatureForFileCommands()) {
        const messageKey = actionType === 'enable' ? 'ENABLE_ARTIFACTS' : 'DISABLE_ARTIFACTS'
        try {
          const validation = await signAction('ADMIN_ACTION', messageKey)
          return await updateArtifacts(validation as ESignature)
        } catch (error) {
          // E-signature cancelled or failed
          return
        }
      } else {
        return await updateArtifacts()
      }
    }
    setSteps(currentStep)
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{ actionType }}
      onSubmit={(values) => {
        save()
      }}
    >
      {({ handleSubmit }) => (
        <Modal
          show={isOpen}
          handleClose={doClose}
          className="modal-add-remove-artifacts"
          title={t(
            steps === 3 ? 'BROWSER_ACTION_ENABLE_DISABLE_ARTIFACTS_REVIEW' : 'BROWSER_ACTION_ENABLE_DISABLE_ARTIFACTS'
          )}
        >
          <Modal.Body>
            <div className="form-group">
              {steps === 1 && (
                <div className="form-group" id="93bf707f-1b48-402b-90fb-c52dfc0a4b9b">
                  <label className="control-label">{t('ETMF_SELECT_ACTION')}</label>
                  <div className="radio">
                    <label>
                      <input
                        type="radio"
                        name="actionType"
                        id="f047b35e-9251-4a47-a008-b3edfcba0ccf"
                        checked={actionType === 'enable'}
                        value="enable"
                        onChange={(event) => {
                          setActionType(event.target.value as 'enable' | 'disable')
                        }}
                      />
                      {t('ETMF_UPDATE_STUDY_STRUCTURE_ENABLE')}
                    </label>
                  </div>
                  <div className="radio">
                    <label>
                      <input
                        type="radio"
                        name="actionType"
                        id="c6aac33e-9c55-4b0b-8408-d29fd43f9ef0"
                        checked={actionType === 'disable'}
                        value="disable"
                        onChange={(event) => {
                          setActionType(event.target.value as 'enable' | 'disable')
                        }}
                      />
                      {t('ETMF_UPDATE_STUDY_STRUCTURE_DISABLE')}
                    </label>
                  </div>
                </div>
              )}

              {steps === 2 && (
                <div className="form-group" id="a8396a23-e39e-4daa-812b-4fa9dffa095d">
                  <label className="fs12 mb0">
                    {t(actionType === 'enable' ? 'SELECT_ARTIFACTS_TO_ENABLE' : 'SELECT_ARTIFACTS_TO_DISABLE')}
                  </label>
                  <div className="table-scroll">
                    <table className="fsiTable styled-table chita-table">
                      <thead>
                        <tr className="settings">
                          <th className="has-checkbox">
                            <input
                              checked={isAllSelected(artifacts)}
                              type="checkbox"
                              onChange={(evt) => selectAll(evt?.target?.checked)}
                              id="b40037a9-7b63-481b-a1a1-851fd52fad96"
                            />
                          </th>
                          <th className="artifact" onClick={() => sortBy(artifacts, 'name', true)}>
                            {t('NAME')}
                            <span className="bc-table-sort">
                              &nbsp;
                              <span
                                className={`bc-table-sort-top ${sortDesc ? 'fa fa-caret-down' : 'fa fa-caret-up'}`}
                              ></span>
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {!artifacts || artifacts?.length === 0 ? (
                          <tr>
                            <td colSpan={2} className="text-center">
                              {t('NO_ARTIFACTS_FOUND_TO_UPDATE')}
                            </td>
                          </tr>
                        ) : (
                          artifacts?.map((entry, index) => (
                            <tr key={index}>
                              <td className="has-checkbox">
                                <input
                                  id={`${entry?.name}-${index}`}
                                  checked={entry?.selected}
                                  type="checkbox"
                                  onChange={() => {
                                    switchSelected(entry)
                                  }}
                                  disabled={entry?.disabled}
                                />
                              </td>
                              <td className={`artifact ${entry?.isCopy ? 'is-copy' : ''}`}>
                                <div
                                  id="bee6d787-07bf-44d8-b488-86c12fb86e17"
                                  className="ellipsis"
                                  onClick={() => {
                                    switchSelected(entry)
                                  }}
                                >
                                  {entry?.name}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {showErrorMessage && selectedArtifactsArray?.length === 0 && (
                <div className="form-group" id="8ecd2deb-238b-4bdc-95c1-6ffed18b661d">
                  <label className="control-label error-message">{t('ARTIFACTS_SELECT_ERROR_MESSAGE')}</label>
                </div>
              )}

              {steps === 3 && selectedArtifactsArray?.length > 0 && (
                <div className="form-group" id="c32119a4-9f32-460f-bb1e-07974feabbb5">
                  <p className="fs12">{t('ARTIFACTS_ENABLE_DISABLE_CONFIRMATION_MESSAGE')}</p>
                  <label className="fs12 mb0">{t('FILE_PLAN')}</label>
                  <input
                    type="text"
                    name="filePlan"
                    id="368aca68-1295-4f93-a406-a06d1d256f4a"
                    disabled
                    value={file.name}
                    className="text-area-style form-control"
                  />
                  <label className="fs12 mb0">
                    {t(actionType === 'enable' ? 'ARTIFACTS_TO_BE_ENABLED' : 'ARTIFACTS_TO_BE_DISABLED')}
                  </label>
                  <textarea
                    name="filePlanName"
                    rows={2}
                    id="095e3102-9830-465c-8716-255dd82a3f8c"
                    disabled
                    value={selectedArtifactsArray.map((entry) => entry?.name).join('\n')}
                    className="text-area-style bg-color-grey form-control"
                  />
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              data-action="ok"
              className="btn btn-link"
              onClick={doClose}
              id="ca27d222-16e1-4b00-9418-d7fefbd5da50"
            >
              {t('CANCEL')}
            </button>
            {steps !== 1 && (
              <button
                data-action="ok"
                className="btn btn-primary"
                onClick={back}
                id="d4378911-0708-4eec-990f-dffffa5cc33f"
              >
                {t('ETMF_GENERATE_TRIAL_STRCT_BTN_BACK')}
              </button>
            )}
            <button
              data-action="ok"
              className="btn btn-primary"
              onClick={() => handleSubmit()}
              id="274a8abd-fb40-4f8d-b53a-20220430fc1c"
            >
              {t(steps === 3 ? 'SAVE_BTN' : 'NEXT_BUTTON')}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}

export default EnableDisableArtifactsModal
